// _feel.js — ASK-142 R7: the frame-rate-independent half of "Fortnite-smooth". PURE (no THREE, no DOM)
// so the maths unit-tests headlessly (scripts/test-feel.mjs).
//
// Two behaviours, both read by stage.js every frame:
//   • LOOK SMOOTHING — the pointer (mouse or finger) writes a TARGET yaw/pitch; the camera eases toward
//     it with `v = lerp(target, v, exp2(-dt / halfLife))`. That is Driscoll's / Holmér's frame-rate-
//     independent damping: two 30 fps frames land exactly where four 60 fps frames do, which a
//     per-frame `lerp(v, target, 0.2)` never does (it is twice as sluggish at 30 fps — on a phone that
//     is the frame rate that most needs to feel crisp). Half-life ≈ 40 ms: 94 % of a flick lands in
//     160 ms, short enough that it reads as smooth, not as lag.
//   • WALK RAMPS — velocity accelerates to and brakes from the input over ≈ 150 ms (Unreal
//     CharacterMovement's MaxAcceleration / BrakingDeceleration: a linear ramp, not an ease), instead
//     of jumping 0 → full on the first frame a key or the stick goes down.

export const LOOK_HALF_LIFE_S = 0.040;
export const WALK_RAMP_S = 0.150;

/** Frame-rate-independent damping of `current` toward `target`: lerp(target, current, 2^(-dt/halfLife)).
 *  dt ≤ 0 leaves it where it is; halfLife ≤ 0 snaps (smoothing off). */
export function damp(current, target, halfLifeS, dt) {
	if (!(halfLifeS > 0)) return target;
	if (!(dt > 0)) return current;
	const k = Math.pow(2, -dt / halfLifeS);
	return target + (current - target) * k;
}

/** One look-smoothing step. `look.yaw/pitch` is the TARGET (what the pointer asked for — the rig's
 *  intent, which every reader of `_mouseLook` has always meant); `look.viewYaw/viewPitch` is what the
 *  camera shows, eased toward it. Returns `settled` when both are within `eps` rad — the caller then
 *  SNAPS and stops writing the camera, so the smoother never holds the camera against another writer
 *  (a camera animation, a respawn, a hold). */
export function stepLook(look, dt, halfLifeS = LOOK_HALF_LIFE_S, eps = 1e-4) {
	look.viewYaw = damp(look.viewYaw, look.yaw, halfLifeS, dt);
	look.viewPitch = damp(look.viewPitch, look.pitch, halfLifeS, dt);
	const settled = Math.abs(look.yaw - look.viewYaw) < eps && Math.abs(look.pitch - look.viewPitch) < eps;
	if (settled) { look.viewYaw = look.yaw; look.viewPitch = look.pitch; }
	return settled;
}

/** Move a velocity vector {x, y, z} toward `target` by at most `maxStep` (vector move-towards — the
 *  direction turns in one step, only the magnitude is rate-limited). Mutates and returns `v`. */
export function moveTowards(v, target, maxStep) {
	const dx = target.x - v.x, dy = target.y - v.y, dz = target.z - v.z;
	const d = Math.hypot(dx, dy, dz);
	if (d <= maxStep || d === 0) { v.x = target.x; v.y = target.y; v.z = target.z; return v; }
	const k = maxStep / d;
	v.x += dx * k; v.y += dy * k; v.z += dz * k;
	return v;
}

/** One walk-ramp step. `v` and `target` are velocities in the walker's own frame (x strafe, y forward,
 *  z vertical) in m/s; `walkSpeed` is the walker's plain (non-sprint) top speed. The rate is a constant
 *  acceleration — max(|target|, walkSpeed) / rampS — Unreal's MaxAcceleration/BrakingDeceleration shape
 *  rather than an ease, so a stop really reaches zero: a walk starts and stops in `rampS`, a sprint
 *  starts in `rampS` and, released, carries its extra speed a little longer. Returns `v`. */
export function stepWalkVelocity(v, target, dt, walkSpeed, rampS = WALK_RAMP_S) {
	if (!(rampS > 0)) { v.x = target.x; v.y = target.y; v.z = target.z; return v; }
	const rate = Math.max(Math.hypot(target.x, target.y, target.z), walkSpeed || 0) / rampS;
	return moveTowards(v, target, rate * Math.max(0, dt));
}

/** ASK-139 §94.10 ruling 2 — NO POST-LIFT DRIFT ON TOUCH. When a look finger lifts, the eased target
 *  snaps to what the camera shows NOW and the ease stops: the view rests where the thumb left it
 *  (Fortnite has no post-lift drift; CoD Mobile ships camera inertia off). The in-drag R7 curve and the
 *  mouse path are untouched — only a touch lift calls this. Mutates and returns `look`. */
export function settleLook(look) {
	look.yaw = look.viewYaw;
	look.pitch = look.viewPitch;
	look.easing = false;
	return look;
}

/** ASK-139 §94.10 ruling 3 — the held item's PINCH: two fingers on the canvas scale the zoom by the ratio
 *  of their start distance to their current distance (spread = closer, since `zoom` multiplies the framed
 *  distance), clamped to the hero's band. A degenerate distance changes nothing. */
export function pinchZoom(zoom0, d0, d1, min, max) {
	if (!(d0 > 0) || !(d1 > 0)) return zoom0;
	return Math.max(min, Math.min(max, zoom0 * (d0 / d1)));
}
